import React from 'react';
import styled from 'styled-components';
import { string, bool, number } from 'prop-types';
import { ToastContainer, toast as reactToast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.min.css';
import { ErrorIcon, SuccessIcon, InfoIcon, AlertCircleOutline } from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';

const INLINE_FIELD_VARIANT = 'inline-field';

const inlineFieldBase = {
    position: 'bottom-right',
    closeButton: false,
    hideProgressBar: true,
    autoClose: 5000,
};

const inlineFieldBaseStyle = {
    width: '320px',
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: '150%',
    padding: '1rem 1.5rem',
    borderRadius: '0.25rem',
};

const inlineFieldBodyStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    margin: 0,
    padding: 0,
};

const getInlineFieldSuccessOptions = () => ({
    ...inlineFieldBase,
    icon: <SuccessIcon color={DEFAULT_COLORS.SUCCESS_GREEN_500} height="32px" width="32px" />,
    style: {
        ...inlineFieldBaseStyle,
        background: DEFAULT_COLORS.SUCCESS_GREEN_100,
        color: DEFAULT_COLORS.SUCCESS_GREEN_500,
    },
    bodyStyle: inlineFieldBodyStyle,
});

const getInlineFieldErrorOptions = () => ({
    ...inlineFieldBase,
    icon: <AlertCircleOutline color={DEFAULT_COLORS.ERROR_RED_500} height="32px" width="32px" />,
    style: {
        ...inlineFieldBaseStyle,
        background: DEFAULT_COLORS.ERROR_RED_100,
        color: DEFAULT_COLORS.ERROR_RED_500,
    },
    bodyStyle: inlineFieldBodyStyle,
});

const ToastContainerStyled = styled(ToastContainer)`
    && {
        width: auto;
        max-width: 100vw;
    }

    .Toastify__toast--default {
        ${({ commonStyles }) => commonStyles}
        ${({ defaultStyles }) => defaultStyles}
    }
    .Toastify__toast--error {
        ${({ commonStyles }) => commonStyles}
        ${({ errorStyles }) => errorStyles}
    }
    .Toastify__toast--warning {
        ${({ commonStyles }) => commonStyles}
        ${({ warningStyles }) => warningStyles}
    }
    .Toastify__toast--success {
        ${({ commonStyles }) => commonStyles}
        ${({ successStyles }) => successStyles}
    }
    .Toastify__toast--info {
        ${({ commonStyles }) => commonStyles}
        ${({ infoStyles }) => infoStyles}
    }
    .Toastify__progress-bar--default {
        background: rgba(255, 255, 255, 0.7);
        ${({ progressBarStyles }) => progressBarStyles}
    }
`;

const resolveOptions = (defaultOptions, options) => {
    if (options?.variant === INLINE_FIELD_VARIANT) {
        const { variant, ...rest } = options;
        return { ...defaultOptions, ...rest };
    }
    return { ...defaultOptions, ...options };
};

const getInlineFieldWarnOptions = () => ({
    ...inlineFieldBase,
    icon: (
        <AlertCircleOutline color={DEFAULT_COLORS.WARNING_YELLOW_500} height="32px" width="32px" />
    ),
    style: {
        ...inlineFieldBaseStyle,
        background: DEFAULT_COLORS.WARNING_YELLOW_100,
        color: DEFAULT_COLORS.WARNING_YELLOW_500,
    },
    bodyStyle: inlineFieldBodyStyle,
});

const getInlineFieldInfoOptions = () => ({
    ...inlineFieldBase,
    icon: <InfoIcon color={DEFAULT_COLORS.INFORMATION_BLUE_500} height="32px" width="32px" />,
    style: {
        ...inlineFieldBaseStyle,
        background: DEFAULT_COLORS.INFORMATION_BLUE_100,
        color: DEFAULT_COLORS.INFORMATION_BLUE_500,
    },
    bodyStyle: inlineFieldBodyStyle,
});

const toast = {
    ...reactToast,
    success: (content, options) => {
        const defaults =
            options?.variant === INLINE_FIELD_VARIANT
                ? getInlineFieldSuccessOptions()
                : { icon: <SuccessIcon color="white" height="24px" width="24px" /> };
        return reactToast.success(content, resolveOptions(defaults, options));
    },
    warn: (content, options) => {
        const defaults =
            options?.variant === INLINE_FIELD_VARIANT
                ? getInlineFieldWarnOptions()
                : { icon: <AlertCircleOutline color="none" height="24px" width="24px" /> };
        return reactToast.warn(content, resolveOptions(defaults, options));
    },
    error: (content, options) => {
        const defaults =
            options?.variant === INLINE_FIELD_VARIANT
                ? getInlineFieldErrorOptions()
                : { icon: <ErrorIcon color="white" height="24px" width="24px" /> };
        return reactToast.error(content, resolveOptions(defaults, options));
    },
    info: (content, options) => {
        const defaults =
            options?.variant === INLINE_FIELD_VARIANT
                ? getInlineFieldInfoOptions()
                : { icon: <InfoIcon color="none" height="24px" width="24px" /> };
        return reactToast.info(content, resolveOptions(defaults, options));
    },
};
const Toaster = props => {
    return <ToastContainerStyled {...props} />;
};

Toaster.propTypes = {
    autoClose: number,
    closeOnClick: bool,
    draggable: bool,
    hideProgressBar: bool,
    newestOnTop: bool,
    pauseOnFocusLoss: bool,
    pauseOnHover: bool,
    position: string,
    rtl: bool,
};

Toaster.defaultProps = {
    autoClose: 5000,
    closeOnClick: true,
    draggable: false,
    hideProgressBar: false,
    newestOnTop: true,
    pauseOnFocusLoss: false,
    pauseOnHover: false,
    position: 'top-center',
    rtl: false,
};

export { Toaster, toast };
